/** External */
import _ from "lodash";
import React, { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import { Modal, Button, ToggleSwitch } from "@toddle-edu/ds-web";
import { WarningTriangleFilled } from "@toddle-edu/ds-icons";
import classNames from "classnames";

/** Internal */
import { graphql, compose } from "lib/apolloClient";
import { updateToddleLibraryPermissionsV2 } from "InternalTools/modules/InternalToolsModules";
import { getInternalToolGenericDataFromCache } from "InternalTools/modules/InternalToolsGraphqlHelper";
import {
  ADD_ON_MANAGER,
  LIBRARY,
  FETCH_LIBRARY_PORTAL_ACTIVATION_STATUS,
} from "InternalTools/modules/InternalToolConstants";
import {
  getPermissionsPayloadForLibraryMutation,
  getLibraryGradesQueryVariables,
  getChangedGrades,
} from "InternalTools/modules/InternalToolUtils";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import { getInternalToolGenericDataQuery } from "InternalTools/modules/InternalToolsQuery";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import Loading from "UIComponents/Loading";
import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import { FormTextInput } from "UIComponents/InputTextField";

/** Relative */
import GradeList from "./components/GradeList";
import classes from "./ManageAccessForToddleLibrary.scss";
import ConfirmationModal from "./components/ConfirmationModal";

const styles = {
  modalBody: { overflow: "hidden" },
  tabContainer: { justifyContent: "flex-start" },
  inputField: { color: "red" },
};

const ManageAccessForToddleLibrary = ({
  closeModal,
  isLoading: isQueryLoading,
  isData,
  data,
  updateToddleLibraryPermissionsV2,
  organizationId,
  curriculumProgramId,
  onSuccessAddOnConfigChange,
  getCurriculumProgramWithLibraryPermissionStatus,
}) => {
  const [curriculumPrograms, setCurriculumPrograms] = useState([]);
  const [grades, setGrades] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isData && !isQueryLoading) {
      const selectedProgram = _.find(
        data,
        program => program.id == curriculumProgramId // using == as for some curriculums id is not string
      );

      if (selectedProgram) {
        const isDpProgram = _.isEqual(
          _.get(selectedProgram, "uid"),
          CURRICULUM_TYPE_DP
        );

        if (isDpProgram && !_.get(selectedProgram, "libraryEnabled")) {
          setShowConfirmationModal(true);
          return;
        }

        setCurriculumPrograms([
          {
            ...selectedProgram,
            libraryEnabled: true,
          },
        ]);
      } else setCurriculumPrograms([]);
    }
  }, [isData, isQueryLoading, data, curriculumProgramId]);

  useEffect(() => {
    const shouldShowError =
      !isQueryLoading &&
      !isData &&
      !_.isEmpty(organizationId) &&
      _.isEmpty(curriculumPrograms);

    setShowError(shouldShowError);
  }, [isQueryLoading, organizationId, curriculumPrograms, isData]);

  const selectedProgram = _.first(curriculumPrograms);

  const onClickCancel = () => {
    setShowConfirmationModal(false);
  };

  const onClickSubmit = () => {
    const selectedProgram = _.find(
      data,
      program => program.id == curriculumProgramId // using == as for some curriculums id is not string
    );

    const programsWithLibraryEnabled = [
      {
        ...selectedProgram,
        libraryEnabled: true,
      },
    ];

    setCurriculumPrograms(programsWithLibraryEnabled);
    setShowConfirmationModal(false);
  };

  const handleToggleCurriculumProgramPermissions = () => {
    const currentLibraryEnabled = _.get(
      selectedProgram,
      "libraryEnabled",
      false
    );

    if (currentLibraryEnabled) setGrades([]);

    setCurriculumPrograms([
      {
        ...selectedProgram,
        libraryEnabled: !currentLibraryEnabled,
      },
    ]);
  };

  const libraryEnabledCurriculumProgramIds = useMemo(() => {
    const isEnabled = _.get(selectedProgram, "libraryEnabled", false);
    return isEnabled ? [_.get(selectedProgram, "id")] : [];
  }, [selectedProgram]);

  const handleUpdateGrade = gradeUid => {
    const updatedGrades = _.map(grades, grade => {
      if (grade.gradeUid === gradeUid) {
        return {
          ...grade,
          isLibraryPremium: !grade.isLibraryPremium,
        };
      }
      return grade;
    });

    setGrades(updatedGrades);
  };

  const handleClickSave = async () => {
    const internalToolsGenericData = getInternalToolGenericDataFromCache(
      getLibraryGradesQueryVariables({
        organizationId,
        curriculumProgramIds: libraryEnabledCurriculumProgramIds,
      })
    );

    const originalGrades = _.get(internalToolsGenericData, "data");
    const addedGrades = getChangedGrades(originalGrades, grades, true);
    const removedGrades = getChangedGrades(originalGrades, grades, false);

    const permissions = getPermissionsPayloadForLibraryMutation(
      curriculumPrograms
    );

    const mutationVariables = {
      apiType: ADD_ON_MANAGER,
      params: {
        organizationId,
        addOnType: LIBRARY,
        input: { permissions, addedGrades, removedGrades },
      },
    };

    const queryVariables = _.get(
      getCurriculumProgramWithLibraryPermissionStatus,
      "variables",
      {}
    );
    try {
      setIsLoading(true);
      await updateToddleLibraryPermissionsV2({
        mutationVariables,
        queryVariables,
      });
      closeModal();
      onSuccessAddOnConfigChange();
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorComponent = () => {
    return showError ? (
      <div className={classNames("text-label-4", classes.errorContainer)}>
        <WarningTriangleFilled
          className={classes.warningIcon}
          variant="critical"
          size="xxx-small"
        />
        <div>This organization does not exist</div>
      </div>
    ) : null;
  };

  const getCurriculumToggleComponent = () => {
    const labelClasses = classNames(
      classes.curriculumToggleContainer,
      "text-label"
    );

    return _.isEmpty(selectedProgram) ? null : (
      <div className={labelClasses}>
        {_.get(selectedProgram, "label", "")}
        <ToggleSwitch
          onChange={handleToggleCurriculumProgramPermissions}
          checked={!_.isEmpty(libraryEnabledCurriculumProgramIds)}
        />
      </div>
    );
  };

  return (
    <>
      <Modal width="800px" height="90%" onClose={closeModal}>
        <Modal.Header title="Enable/Disable Toddle Library" isClosable />
        <Modal.Body isScrollable style={styles.modalBody}>
          {isQueryLoading ? (
            <Loading />
          ) : (
            <div className={classes.container}>
              <FormTextInput
                style={styles}
                size="large"
                placeholder="Enter The Organization ID"
                label="Organization ID"
                value={organizationId}
                isRequired
                updateInputField={() => {}}
                disabled={true}
              />

              {getErrorComponent()}
              {getCurriculumToggleComponent()}

              {_.isEmpty(libraryEnabledCurriculumProgramIds) ? null : (
                <GradeList
                  organizationId={organizationId}
                  curriculumProgramIds={libraryEnabledCurriculumProgramIds}
                  grades={grades}
                  setGrades={setGrades}
                  onUpdateGrade={handleUpdateGrade}
                />
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.footer}>
            <Button
              _legacy_variant="outlined-subtle"
              variant="neutral"
              type="outlined"
              size="large"
              onClick={closeModal}
            >
              Cancel
            </Button>
            <Button
              variant="progressive"
              size="large"
              onClick={handleClickSave}
            >
              Save
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {showConfirmationModal ? (
        <ConfirmationModal
          onClickCancel={onClickCancel}
          onClickSubmit={onClickSubmit}
        />
      ) : null}

      {isLoading ? <FullScreenLoader /> : null}
    </>
  );
};

const mapActionCreators = {
  updateToddleLibraryPermissionsV2,
};

export default compose(
  connect(null, mapActionCreators),
  graphql(getInternalToolGenericDataQuery, {
    name: "getCurriculumProgramWithLibraryPermissionStatus",
    options: ({ organizationId }) => ({
      variables: {
        input: {
          apiType: ADD_ON_MANAGER,
          params: {
            organizationId,
            addOnType: LIBRARY,
            queryType: FETCH_LIBRARY_PORTAL_ACTIVATION_STATUS,
          },
        },
      },
      fetchPolicy: "cache-and-network",
    }),
    props: ({ getCurriculumProgramWithLibraryPermissionStatus }) => {
      const internalToolsGenericData = getInternalToolGenericDataFromCache(
        getCurriculumProgramWithLibraryPermissionStatus.variables
      );

      const data = _.get(internalToolsGenericData, "data.curriculumPrograms");

      return {
        data,
        getCurriculumProgramWithLibraryPermissionStatus,
        isData: !_.isEmpty(data),
        isLoading: _.includes(
          GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
          getCurriculumProgramWithLibraryPermissionStatus.networkStatus
        ),
      };
    },
  })
)(ManageAccessForToddleLibrary);
